import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths } from './schema';

export type Schemas = components['schemas'];
export type ErrorCode = NonNullable<NonNullable<Schemas['Error']['error']>['code']>;

/** An API error with the server's code and message, for showing to the user. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Supplies the headers that identify the caller and the business on every request. */
export type AuthHeaders = () => Promise<Record<string, string>>;
let authHeaders: AuthHeaders = async () => ({});
let onUnauthenticated: () => void = () => {};

export function configureApi(headers: AuthHeaders, unauthenticated: () => void) {
  authHeaders = headers;
  onUnauthenticated = unauthenticated;
}

const auth: Middleware = {
  async onRequest({ request }) {
    for (const [name, value] of Object.entries(await authHeaders())) request.headers.set(name, value);
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) onUnauthenticated();
    return response;
  },
};

export const api = createClient<paths>({ baseUrl: '/' });
api.use(auth);

/** Throws an ApiError for a failed call and returns the `data` envelope of a successful one. */
export async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await call;
  if (error !== undefined || !response.ok) {
    const body = (error ?? {}) as { error?: { code?: string; message?: string } };
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`);
  }
  return data as T;
}

/** Fetches a binary response (an invoice PDF) with the same auth headers. */
export async function fetchBlob(path: string): Promise<Blob> {
  const response = await fetch(path, { headers: await authHeaders() });
  if (response.status === 401) onUnauthenticated();
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`);
  }
  return response.blob();
}

/** Sends a multipart form (a file upload) with the same auth headers, returning the JSON body. */
export async function sendForm<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const response = await fetch(path, { method, headers: await authHeaders(), body: form });
  if (response.status === 401) onUnauthenticated();
  const body = (await response.json().catch(() => ({}))) as { data?: T; error?: { code?: string; message?: string } };
  if (!response.ok) {
    throw new ApiError(response.status, body.error?.code ?? 'internal_error', body.error?.message ?? `Request failed (${response.status})`);
  }
  return body.data as T;
}
